// Dynamic base URL configuration
const getBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname.includes('localhost')) return 'http://localhost:5003';
    if (hostname.includes('staging')) return 'https://staging.api.podcast-generator.ai';
  }
  return 'https://api.podcast-generator.ai';
};

// API Configuration
export const API_CONFIG = {
  BASE_URL: getBaseUrl(),
  ENDPOINTS: {
    AUTH: {
      SIGNUP: '/api/auth/signup',
      SIGNIN: '/api/auth/signin',
      SIGNOUT: '/api/auth/signout',
    },
    USER: {
      PROFILE: '/api/user/profile',
    },
  },
} as const

export class ApiError extends Error {
  public status: number
  public statusText: string
  public data: unknown
  
  constructor(
    message: string,
    status: number,
    statusText: string,
    data: unknown = null
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.statusText = statusText
    this.data = data
  }
}

export const createApiUrl = (endpoint: string): string => {
  return `${getBaseUrl()}${endpoint}`
}

export const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const url = createApiUrl(endpoint)
  
  const config: RequestInit = {
    credentials: 'include', // Include cookies for authentication
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  }

  const response = await fetch(url, config)

  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`
    let emailVerificationRequired = false
    let errorData: unknown = null
    
    try {
      const parsed = await response.json()
      errorData = parsed
      errorMessage = parsed.error || parsed.message || errorMessage
      emailVerificationRequired = parsed.emailVerificationRequired || false
    } catch {
      // If response is not JSON, use status text
      errorMessage = response.statusText || errorMessage
    }

    // If email verification is required, redirect to verification page
    if (response.status === 403 && emailVerificationRequired) {
      window.location.href = '/?email_verification_required=true'
      return Promise.reject(new ApiError(errorMessage, response.status, response.statusText, errorData))
    }

    throw new ApiError(errorMessage, response.status, response.statusText, errorData)
  }

  if (!response.headers.get('Content-Type')?.includes('application/json')) {
    return response.text() as unknown as T
  }
  return response.json()
}

export const apiRequestForm = async <T>(endpoint: string, formData: FormData, options: RequestInit = {}): Promise<T> => {
  const url = createApiUrl(endpoint)

  // Ensure we do not send a JSON Content-Type and default to POST if not provided
  let sanitizedHeaders: HeadersInit | undefined = options.headers
  if (sanitizedHeaders) {
    const h = new Headers(sanitizedHeaders as HeadersInit)
    h.delete('Content-Type') // browser will set correct multipart boundary
    sanitizedHeaders = h
  }

  const config: RequestInit = {
    credentials: 'include',
    ...options,
    method: options.method ?? 'POST',
    headers: sanitizedHeaders,
    body: formData,
  }

  const response = await fetch(url, config)
  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`
    try {
      const errorData = await response.json()
      errorMessage = errorData.error || errorData.message || errorMessage
    } catch {
      errorMessage = response.statusText || errorMessage
    }
    throw new ApiError(errorMessage, response.status, response.statusText)
  }
  return response.json()
}
